"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

// MP4 (H.264) only, like the card videos: plays everywhere, hardware-decoded on every phone.
export type TechStoryFilm = {
  title: string;
  /** 16:9 cut: desktop, landscape tablets and phones held sideways. */
  desktopMp4: string;
  /** 4:5 cut: phones and tablets in portrait. */
  mobileMp4: string;
};

/** Analytics `video_type`: which CTA opened the film. */
export type FilmVideoType = "header_film" | "pdp_film" | "spectral_mapping_film";

// Same breakpoint as the section's stacked (mobile) layout, portrait only.
const MOBILE_FILM_MEDIA = "(max-width: 1023px) and (orientation: portrait)";

export type StoryFilmDialogProps = {
  film: TechStoryFilm;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Section the play is attributed to (the hero CTA opens the same film). */
  sectionType?: (typeof SectionTypes)[keyof typeof SectionTypes];
  videoType: FilmVideoType;
};

export function StoryFilmDialog({
  film,
  open,
  onOpenChange,
  sectionType = SectionTypes.PRODUCT_TECH_STORIES,
  videoType,
}: StoryFilmDialogProps) {
  const hasTrackedPlay = React.useRef(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Only the film CTAs open the dialog, so each open is one `video_played_clicked`.
  React.useEffect(() => {
    if (open) {
      sendEvent(EventNames.VIDEO_PLAYED_CLICKED, { video_type: videoType });
    }
  }, [open, videoType]);

  // The X button: report how far into the film the viewer got, in seconds.
  const trackStop = () => {
    const seconds = videoRef.current?.currentTime ?? 0;
    sendEvent(EventNames.VIDEO_STOPPED_CLICKED, {
      video_type: videoType,
      watched_timestamp: Math.round(seconds * 10) / 10,
    });
  };

  // The web SDK's floating cookie button (inline z-index 99999) would sit on top of the film.
  // Drop it behind the page while the film is open, and put its own values back on close.
  // z-index alone isn't enough: where nothing opaque sits behind it (only the body's background,
  // e.g. the LP on iPhone) it still shows through the dialog's translucent backdrop, so hide it too.
  React.useEffect(() => {
    if (!open) return;
    const cookieButton = document.getElementById("cookie-floating-button");
    if (!cookieButton) return;
    const { zIndex, visibility } = cookieButton.style;
    cookieButton.style.zIndex = "-1";
    cookieButton.style.visibility = "hidden";
    return () => {
      cookieButton.style.zIndex = zIndex;
      cookieButton.style.visibility = visibility;
    };
  }, [open]);

  // Start explicitly: the `autoplay` attribute isn't reliable for a video mounted inside the dialog.
  // If the browser refuses sound, play muted rather than show a frozen frame; controls can unmute.
  React.useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const video = videoRef.current;
      if (!video) return;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const trackPlay = React.useCallback(() => {
    if (hasTrackedPlay.current) return;
    hasTrackedPlay.current = true;
    sendEvent(EventNames.VIDEO_PLAYED, withSectionType(sectionType));
  }, [sectionType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="tech-stories-film"
        data-testid="tech-stories-film"
        aria-describedby={undefined}
        // w-max: the dialog is anchored at left:50%, so `auto` would cap it (and the video) at half the screen.
        className="w-max max-w-none gap-0 overflow-hidden rounded-2xl border-0 bg-black p-0 sm:max-w-none"
        closeButtonClass="top-3 right-3 z-10 rounded-full bg-black/60 p-1.5 text-white opacity-100 hover:bg-black/80"
        closeButtonClick={trackStop}
      >
        <DialogTitle className="sr-only">{film.title}</DialogTitle>
        {/* Mounted only while open, so closing the dialog stops playback. */}
        <video
          ref={videoRef}
          id="tech-stories-film-video"
          data-testid="tech-stories-film-video"
          autoPlay
          controls
          playsInline
          preload="auto"
          onPlay={trackPlay}
          className="block aspect-video w-[min(92vw,calc(85svh*16/9))] bg-black max-lg:portrait:aspect-[4/5] max-lg:portrait:w-[min(92vw,calc(85svh*4/5))]"
        >
          <source
            src={film.mobileMp4}
            type="video/mp4"
            media={MOBILE_FILM_MEDIA}
          />
          <source src={film.desktopMp4} type="video/mp4" />
        </video>
      </DialogContent>
    </Dialog>
  );
}
